import { Link } from "react-router-dom";
import PageHead from "../components/PageHead";
import CTA, { Arr } from "../components/CTA";

/* ──────────────────────────────────────────────────────────────
   Unified cover system. Every Insights cover shares the same
   frame (off-white paper · fig. number · eyebrow · white data
   panel · caption) so the five reads as one editorial series.
   Only the artwork inside the panel changes.
   ────────────────────────────────────────────────────────────── */

function CoverFrame({ fig, eyebrow, caption, kind, children }) {
  return (
    <div className={`insight__cover cov cov--${kind}`} aria-hidden="true">
      <div className="cov__frame">
        <span className="cov__fig">Fig. {fig}</span>
        <span className="cov__eye">{eyebrow}</span>
      </div>
      <div className="cov__panel">{children}</div>
      <div className="cov__cap">{caption}</div>
    </div>
  );
}

function CoverBottlenecks() {
  return (
    <CoverFrame fig="01" eyebrow="Time per batch" caption="Where the hours actually go" kind="bottlenecks">
      <div className="cov__stack">
        <div className="cov__stack-seg cov__stack-seg--visible" style={{ width: "58%" }}>
          <span>Manual entry</span>
        </div>
        <div className="cov__stack-seg cov__stack-seg--invisible" style={{ width: "42%" }}>
          <span>Pre-sort</span>
        </div>
      </div>
      <div className="cov__keys">
        <div className="cov__key">
          <i className="cov__key-dot cov__key-dot--navy" />
          <span className="cov__key-lab">Visible</span>
          <span className="cov__key-val">4.2h</span>
        </div>
        <div className="cov__key">
          <i className="cov__key-dot cov__key-dot--teal" />
          <span className="cov__key-lab">Invisible</span>
          <span className="cov__key-val">2.8h</span>
        </div>
      </div>
    </CoverFrame>
  );
}

function CoverTrainedVsGeneric() {
  return (
    <CoverFrame fig="02" eyebrow="Accuracy over months" caption="Trained models pull away" kind="chart">
      <svg className="cov__svg" viewBox="0 0 240 110" preserveAspectRatio="none">
        <defs>
          <pattern id="cgrid" width="24" height="22" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 22" fill="none" stroke="#E1E8EA" strokeWidth="0.6"/>
          </pattern>
        </defs>
        <rect x="14" y="10" width="216" height="88" fill="url(#cgrid)" opacity="0.6"/>
        <line x1="14" y1="98" x2="230" y2="98" stroke="#103049" strokeWidth="0.8" />
        <line x1="14" y1="10" x2="14" y2="98" stroke="#103049" strokeWidth="0.8" />
        <line x1="14" y1="52" x2="230" y2="52" stroke="#103049" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
        <path d="M14 60 Q 80 56 120 38 T 230 16" fill="none" stroke="#0E9AA6" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="230" cy="52" r="3" fill="#103049" />
        <circle cx="230" cy="16" r="4" fill="#0E9AA6" />
      </svg>
      <div className="cov__keys">
        <div className="cov__key">
          <i className="cov__key-dot cov__key-dot--navy" />
          <span className="cov__key-lab">Generic</span>
        </div>
        <div className="cov__key">
          <i className="cov__key-dot cov__key-dot--teal" />
          <span className="cov__key-lab">Trained</span>
        </div>
      </div>
    </CoverFrame>
  );
}

function CoverReviewAsFeature() {
  return (
    <CoverFrame fig="03" eyebrow="Confidence per field" caption="One flagged, sent to review" kind="review">
      <div className="cov__rows">
        <div className="cov__row">
          <span className="cov__k">Claimant</span>
          <span className="cov__v">M. Whitlock</span>
          <span className="cov__c cov__c--ok">99.1%</span>
        </div>
        <div className="cov__row cov__row--flag">
          <span className="cov__k">Amount</span>
          <span className="cov__v">£4,182.00</span>
          <span className="cov__c cov__c--warn">63%</span>
        </div>
        <div className="cov__row">
          <span className="cov__k">Date of loss</span>
          <span className="cov__v">14 Mar 2026</span>
          <span className="cov__c cov__c--ok">95.8%</span>
        </div>
      </div>
    </CoverFrame>
  );
}

function CoverAuditByProduct() {
  const rows = [
    ["12:04:18", "Field updated"],
    ["12:04:31", "Confidence raised"],
    ["12:05:02", "Reviewed, approved"],
    ["12:05:09", "Delivered to DMS"],
  ];
  return (
    <CoverFrame fig="04" eyebrow="Audit log" caption="Captured at every step" kind="audit">
      <div className="cov__rows">
        {rows.map(([t, what], i) => (
          <div className="cov__row cov__row--log" key={i}>
            <span className="cov__k">{t}</span>
            <span className="cov__v cov__v--log">{what}</span>
            <span className="cov__c cov__c--ok">·</span>
          </div>
        ))}
      </div>
    </CoverFrame>
  );
}

function CoverPilotPlaybook() {
  const phases = [
    { label: "W1", phase: 1 },
    { label: "W2", phase: 1 },
    { label: "W3", phase: 2 },
    { label: "W4", phase: 2 },
    { label: "W5", phase: 3 },
    { label: "W6", phase: 3 },
  ];
  return (
    <CoverFrame fig="05" eyebrow="Pilot timeline" caption="Train · Run · Decide" kind="pilot">
      <div className="cov__timeline">
        <div className="cov__track" />
        <div className="cov__phases">
          {phases.map((w, i) => (
            <div className={`cov__phase cov__phase--p${w.phase}`} key={i}>
              <span className="cov__phase-dot" />
              <span className="cov__phase-lab">{w.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="cov__keys cov__keys--phases">
        <div className="cov__key"><i className="cov__key-dot cov__key-dot--teal" /><span className="cov__key-lab">Train</span></div>
        <div className="cov__key"><i className="cov__key-dot cov__key-dot--ochre" /><span className="cov__key-lab">Run</span></div>
        <div className="cov__key"><i className="cov__key-dot cov__key-dot--ok" /><span className="cov__key-lab">Decide</span></div>
      </div>
    </CoverFrame>
  );
}

const COVERS = {
  bottlenecks: CoverBottlenecks,
  chart: CoverTrainedVsGeneric,
  review: CoverReviewAsFeature,
  audit: CoverAuditByProduct,
  pilot: CoverPilotPlaybook,
};

function Cover({ type }) {
  const C = COVERS[type] || CoverBottlenecks;
  return <C />;
}

/* ────────────────────────────────────────────────────────────── */

const POSTS = [
  {
    id: "two-bottlenecks",
    cat: "Field notes",
    date: "Jan 2026",
    read: "6 min read",
    title: "The two bottlenecks every document team has, and why one is invisible.",
    excerpt: "Manual entry gets all the attention. The pre-sort step quietly eats just as much time, and almost nobody measures it.",
    variant: "",
    cover: "bottlenecks",
    href: "/insights/two-bottlenecks",
  },
  {
    id: "trained-vs-generic-idp",
    cat: "Point of view",
    date: "Jan 2026",
    read: "5 min read",
    title: "Trained vs. generic IDP: why the model you build always wins.",
    excerpt: "Generic models guess. Trained models say what they know. Here's what that difference does to your accuracy curve.",
    variant: "accent",
    cover: "chart",
    href: "/insights/trained-vs-generic-idp",
  },
  {
    id: "review-is-a-feature",
    cat: "Product",
    date: "Dec 2025",
    read: "4 min read",
    title: "Review is a feature, not a failure.",
    excerpt: "Why we route flagged work to a human instead of forcing a guess, and why every correction makes the next document easier.",
    variant: "",
    cover: "review",
    href: "/insights/review-is-a-feature",
  },
  {
    id: "audit-trail-by-product",
    cat: "Compliance",
    date: "Dec 2025",
    read: "5 min read",
    title: "How to make the audit trail a by-product of the work.",
    excerpt: "If the platform logs what happened, when, and by whom, compliance stops being a separate project.",
    variant: "",
    cover: "audit",
    href: "/insights/audit-trail-by-product",
  },
  {
    id: "pilot-playbook",
    cat: "Sales playbook",
    date: "Nov 2025",
    read: "7 min read",
    title: "A 4-to-6 week pilot playbook for IDP.",
    excerpt: "The exact framework we use with new customers: two or three document types, one measured baseline, a clear automation gain.",
    variant: "",
    cover: "pilot",
    href: "/insights/idp-pilot-playbook",
  },
];

export default function Insights() {
  const [featured, ...rest] = POSTS;
  const FeatLink = featured.href.startsWith("/") ? Link : "a";
  const featProp = featured.href.startsWith("/") ? { to: featured.href } : { href: featured.href };
  return (
    <>
      <PageHead
        crumb="Insights"
        eyebrow="Insights"
        title="Notes from the document trenches."
        lede="Short, honest pieces on intelligent document processing: what we've learned shipping Docket, what we'd tell a buyer, and what we'd never tell a vendor."
      />

      <section className="section">
        <div className="container">
          <div className="insight-grid reveal">
            <article className={`insight insight--featured insight--${featured.variant || "plain"}`} data-testid={`insight-${featured.id}`}>
              <Cover type={featured.cover} />
              <div className="insight__body">
                <div className="insight__meta">
                  <span>{featured.cat}</span><i /><span>{featured.date}</span><i /><span>{featured.read}</span>
                </div>
                <h3>{featured.title}</h3>
                <p>{featured.excerpt}</p>
                <FeatLink {...featProp} className="insight__read" data-testid="insight-featured-link">Read the piece <Arr /></FeatLink>
              </div>
            </article>

            {rest.map((p) => {
              const isRoute = p.href.startsWith("/");
              const LinkEl = isRoute ? Link : "a";
              const linkProps = isRoute ? { to: p.href } : { href: p.href };
              return (
                <article key={p.id} className={`insight ${p.variant ? `insight--${p.variant}` : ""}`} data-testid={`insight-${p.id}`}>
                  <Cover type={p.cover} />
                  <div className="insight__body">
                    <div className="insight__meta">
                      <span>{p.cat}</span><i /><span>{p.date}</span><i /><span>{p.read}</span>
                    </div>
                    <h3>{p.title}</h3>
                    <p>{p.excerpt}</p>
                    <LinkEl {...linkProps} className="insight__read">Read more <Arr /></LinkEl>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Subscribe */}
          <div className="reveal" style={{ marginTop: 40 }}>
            <div className="subscribe">
              <div>
                <h3>Get one short note a month.</h3>
                <p>No newsletter sludge. Just one well-thought piece on document processing, when we have something worth saying.</p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = e.target.email.value.trim();
                  window.location.href = `mailto:hello@devnco.in?subject=${encodeURIComponent("Subscribe me to Docket Insights")}&body=${encodeURIComponent("Please add " + email + " to the Docket Insights list.")}`;
                }}
                data-testid="subscribe-form"
              >
                <input name="email" type="email" placeholder="you@company.com" required data-testid="subscribe-email" />
                <button type="submit" className="btn btn--primary" data-testid="subscribe-submit">Subscribe <Arr /></button>
              </form>
            </div>
          </div>

          <div className="reveal" style={{ marginTop: 28 }}>
            <Link to="/contact" className="txtlink">Talk to us instead <Arr /></Link>
          </div>
        </div>
      </section>

      <CTA
        eyebrow="See it on your documents"
        title='Reading is good. <em>Seeing</em> Docket run is better.'
        text="A pilot puts Docket in front of the documents your team actually handles. It runs for four to six weeks and ends with a measured accuracy baseline on your data."
      />
    </>
  );
}
